import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiTrash2, FiCheck, FiX, FiDollarSign, FiEye, FiEdit2, FiCopy, FiSave, FiPrinter, FiSend } from 'react-icons/fi';

const TABS = ['Suppliers', 'Bills', 'Expense Claims', 'Payments'];
const emptyBillLine = { description: '', quantity: '1', unitPrice: '0.00', vatRateId: '' };
const emptyBillForm = { supplierId: '', supplierReference: '', billDate: new Date().toISOString().slice(0, 10), dueDate: '', lines: [{ ...emptyBillLine }] };

const printStyles = `
  @media print {
    @page { size: A4; margin: 1.2cm; }
    body * { visibility: hidden; }
    .bill-print-area, .bill-print-area * { visibility: visible; }
    .bill-print-area {
      position: absolute; left: 0; top: 0; width: 100%; padding: 0;
      font-size: 11px;
    }
    .no-print { display: none !important; }
    .bill-print-area .table-container {
      overflow: visible !important;
      border: 1px solid #d1d5db;
    }
    .bill-print-area table.data-table {
      display: table !important; width: 100% !important;
      table-layout: fixed !important; border-collapse: collapse !important;
    }
    .bill-print-area table.data-table thead { display: table-header-group !important; }
    .bill-print-area table.data-table tbody { display: table-row-group !important; }
    .bill-print-area table.data-table tr { display: table-row !important; }
    .bill-print-area table.data-table th,
    .bill-print-area table.data-table td {
      display: table-cell !important; padding: 0.4rem 0.6rem; font-size: 11px;
    }
  }
`;

const AP = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Suppliers');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [organisation, setOrganisation] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [bills, setBills] = useState([]);
  const [expenseClaims, setExpenseClaims] = useState([]);
  const [payments, setPayments] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [billWorkflowId, setBillWorkflowId] = useState(null);
  const [billApprovalRequests, setBillApprovalRequests] = useState([]);
  const [claimWorkflowId, setClaimWorkflowId] = useState(null);
  const [claimApprovalRequests, setClaimApprovalRequests] = useState([]);

  const [supplierForm, setSupplierForm] = useState({ name: '', email: '', phone: '', paymentTermsDays: 30, vatNumber: '', currencyCode: '' });
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [editSupplierForm, setEditSupplierForm] = useState(null);

  const [billForm, setBillForm] = useState(emptyBillForm);
  const [editingBillId, setEditingBillId] = useState(null); // null = creating (or copying); set = editing that draft

  const [paymentForm, setPaymentForm] = useState({ supplierId: '', amount: '', paymentMethod: 'bank_transfer', paidAt: new Date().toISOString().slice(0, 10), billId: '' });

  const [selectedBill, setSelectedBill] = useState(null);
  const [billLines, setBillLines] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/ap/suppliers'),
      apiClient.get('/ap/bills'),
      apiClient.get('/expenses'),
      apiClient.get('/ap/payments'),
      apiClient.get('/vat/transactions/rates'),
      apiClient.get('/lookups/currencies'),
      apiClient.get('/organisations/current'),
      apiClient.get('/workflow/workflows', { params: { appliesToType: 'bill' } }).catch(() => ({ data: { data: [] } })),
      apiClient.get('/workflow/approvals').catch(() => ({ data: { data: [] } })),
      apiClient.get('/workflow/workflows', { params: { appliesToType: 'expense_claim' } }).catch(() => ({ data: { data: [] } })),
    ]).then(([supRes, billRes, claimRes, payRes, vatRes, currRes, orgRes, wfRes, reqRes, claimWfRes]) => {
      setSuppliers(supRes.data.data);
      setBills(billRes.data.data);
      setExpenseClaims(claimRes.data.data);
      setPayments(payRes.data.data);
      setVatRates(vatRes.data.data);
      setCurrencies(currRes.data.data);
      setOrganisation(orgRes.data.data);
      const activeBillWorkflow = wfRes.data.data.find((w) => w.isActive);
      setBillWorkflowId(activeBillWorkflow ? activeBillWorkflow.id : null);
      setBillApprovalRequests(reqRes.data.data.filter((r) => r.entityType === 'bill'));
      const activeClaimWorkflow = claimWfRes.data.data.find((w) => w.isActive);
      setClaimWorkflowId(activeClaimWorkflow ? activeClaimWorkflow.id : null);
      setClaimApprovalRequests(reqRes.data.data.filter((r) => r.entityType === 'expense_claim'));
    }).catch((err) => showError(getErrorMessage(err, 'Could not load AP data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const findSupplier = (id) => suppliers.find((s) => s.id === id);
  const supplierName = (id) => findSupplier(id)?.name || id;
  const symbolForCurrencyId = (currencyId) => currencies.find((c) => c.id === currencyId)?.symbol || '£';
  const symbolForSupplier = (supplierId) => symbolForCurrencyId(findSupplier(supplierId)?.currencyId);
  const companyName = organisation?.tradingName || organisation?.legalName || 'Your Company';
  const handlePrint = () => window.print();
  const approvalForBill = (billId) => billApprovalRequests.find((r) => r.entityId === billId);
  const approvalForClaim = (claimId) => claimApprovalRequests.find((r) => r.entityId === claimId);

  // ---- Suppliers ----
  const handleAddSupplier = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...supplierForm };
      if (!payload.currencyCode) delete payload.currencyCode;
      await apiClient.post('/ap/suppliers', payload);
      success('✅ Supplier added');
      setShowForm(false);
      setSupplierForm({ name: '', email: '', phone: '', paymentTermsDays: 30, vatNumber: '', currencyCode: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add supplier')); }
  };

  const startEditSupplier = (s) => {
    setEditingSupplierId(s.id);
    setEditSupplierForm({ name: s.name, email: s.email || '', phone: s.phone || '', paymentTermsDays: s.paymentTermsDays, status: s.status });
  };

  const handleUpdateSupplier = async (e, supplierId) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/ap/suppliers/${supplierId}`, editSupplierForm);
      success('✅ Supplier updated');
      setEditingSupplierId(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update supplier')); }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await apiClient.delete(`/ap/suppliers/${supplierId}`);
      success('✅ Supplier deleted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete supplier')); }
  };

  // ---- Bills ----
  const updateBillLine = (idx, field, value) => {
    setBillForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };
  const addBillLine = () => setBillForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyBillLine }] }));
  const removeBillLine = (idx) => setBillForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
  const billLineTotal = (line) => (Number(line.quantity || 0) * Number(line.unitPrice || 0)).toFixed(2);
  const billFormTotal = billForm.lines.reduce((sum, l) => sum + Number(billLineTotal(l)), 0).toFixed(2);
  const billFormSymbol = symbolForSupplier(billForm.supplierId);

  const closeBillForm = () => {
    setShowForm(false);
    setEditingBillId(null);
    setBillForm(emptyBillForm);
  };

  const handleSubmitBill = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...billForm };
      if (!payload.supplierReference) delete payload.supplierReference;
      if (!payload.dueDate) delete payload.dueDate;
      payload.lines = billForm.lines.map((l) => ({
        description: l.description, quantity: l.quantity, unitPrice: l.unitPrice,
        ...(l.vatRateId ? { vatRateId: l.vatRateId } : {}),
      }));

      if (editingBillId) {
        await apiClient.patch(`/ap/bills/${editingBillId}`, payload);
        success('✅ Bill updated');
      } else {
        await apiClient.post('/ap/bills', payload);
        success('✅ Bill created as draft');
      }
      closeBillForm();
      loadAll();
    } catch (err) { showError(getErrorMessage(err, editingBillId ? 'Could not update bill' : 'Could not create bill')); }
  };

  const startEditBill = async (bill) => {
    try {
      const { data } = await apiClient.get(`/ap/bills/${bill.id}/lines`);
      setBillForm({
        supplierId: bill.supplierId,
        supplierReference: bill.supplierReference || '',
        billDate: bill.billDate,
        dueDate: bill.dueDate || '',
        lines: data.data.map((l) => ({
          description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, vatRateId: l.vatRateId || '',
        })),
      });
      setEditingBillId(bill.id);
      setShowForm(true);
      setSelectedBill(null);
    } catch (err) { showError(getErrorMessage(err, 'Could not load bill for editing')); }
  };

  const startCopyBill = async (bill) => {
    try {
      const { data } = await apiClient.get(`/ap/bills/${bill.id}/lines`);
      setBillForm({
        supplierId: bill.supplierId,
        supplierReference: '',
        billDate: new Date().toISOString().slice(0, 10),
        dueDate: '',
        lines: data.data.map((l) => ({
          description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, vatRateId: l.vatRateId || '',
        })),
      });
      setEditingBillId(null); // copy always creates a NEW bill, never edits the original
      setShowForm(true);
      setSelectedBill(null);
      success('Bill copied into a new draft — adjust the date and save');
    } catch (err) { showError(getErrorMessage(err, 'Could not copy bill')); }
  };

  const viewBill = async (bill) => {
    setSelectedBill(bill);
    setBillLines(null);
    try {
      const { data } = await apiClient.get(`/ap/bills/${bill.id}/lines`);
      setBillLines(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load bill lines')); }
  };

  const handleApproveBill = async (billId) => {
    try {
      await apiClient.post(`/ap/bills/${billId}/approve`);
      success('✅ Bill approved');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not approve bill')); }
  };

  const handleDeleteDraftBill = async (billId) => {
    if (!window.confirm('Delete this draft bill?')) return;
    try {
      await apiClient.delete(`/ap/bills/${billId}`);
      success('✅ Draft bill deleted');
      if (selectedBill?.id === billId) setSelectedBill(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete bill')); }
  };

  const handleSubmitForApproval = async (bill) => {
    if (!billWorkflowId) return;
    try {
      await apiClient.post('/workflow/approvals', {
        workflowId: billWorkflowId,
        entityType: 'bill',
        entityId: bill.id,
        amount: bill.total,
      });
      success('✅ Submitted for approval');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not submit for approval')); }
  };

  // ---- Expense Claims ----
  const handleApproveClaim = async (claimId) => {
    try {
      await apiClient.post(`/expenses/${claimId}/approve`);
      success('✅ Claim approved');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not approve claim')); }
  };

  const handleRejectClaim = async (claimId) => {
    try {
      await apiClient.post(`/expenses/${claimId}/reject`, {});
      success('✅ Claim rejected');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not reject claim')); }
  };

  const handlePayClaim = async (claimId) => {
    try {
      await apiClient.post(`/expenses/${claimId}/pay`);
      success('✅ Claim marked paid');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not mark claim paid')); }
  };

  const handleSubmitClaimForApproval = async (claim) => {
    if (!claimWorkflowId) return;
    try {
      await apiClient.post('/workflow/approvals', {
        workflowId: claimWorkflowId,
        entityType: 'expense_claim',
        entityId: claim.id,
        amount: claim.total,
      });
      success('✅ Submitted for approval');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not submit for approval')); }
  };

  // ---- Payments ----
  const supplierBills = (supplierId) => bills.filter((b) => b.supplierId === supplierId && b.status !== 'paid');

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/ap/payments', {
        supplierId: paymentForm.supplierId,
        amount: paymentForm.amount,
        paymentMethod: paymentForm.paymentMethod,
        paidAt: paymentForm.paidAt,
        allocations: [{ billId: paymentForm.billId, allocatedAmount: paymentForm.amount }],
      });
      success('✅ Payment recorded');
      setShowForm(false);
      setPaymentForm({ supplierId: '', amount: '', paymentMethod: 'bank_transfer', paidAt: new Date().toISOString().slice(0, 10), billId: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not record payment')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <style>{printStyles}</style>

      <div className="page-header no-print">
        <h1>Accounts Payable</h1>
        <p>Suppliers, bills, expense claims, and payments</p>
      </div>

      <div className="no-print" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedBill(null); setEditingSupplierId(null); closeBillForm(); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Suppliers' && (
        <section className="page-section no-print">
          <div className="section-header">
            <h2>Suppliers</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Supplier'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddSupplier} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Name *</label><input required value={supplierForm.name} onChange={(e) => setSupplierForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-field">
                <label>Currency</label>
                <select value={supplierForm.currencyCode} onChange={(e) => setSupplierForm(p => ({ ...p, currencyCode: e.target.value }))}>
                  <option value="">Organisation default</option>
                  {currencies.map((c) => <option key={c.id} value={c.code}>{c.code} ({c.symbol}) — {c.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Email</label><input type="email" value={supplierForm.email} onChange={(e) => setSupplierForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div className="form-field"><label>Phone</label><input value={supplierForm.phone} onChange={(e) => setSupplierForm(p => ({ ...p, phone: e.target.value }))} /></div>
              <div className="form-field"><label>Payment Terms (days)</label><input type="number" value={supplierForm.paymentTermsDays} onChange={(e) => setSupplierForm(p => ({ ...p, paymentTermsDays: Number(e.target.value) }))} /></div>
              <div className="form-field"><label>VAT Number</label><input value={supplierForm.vatNumber} onChange={(e) => setSupplierForm(p => ({ ...p, vatNumber: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Currency</th><th>Terms</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {suppliers.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No suppliers yet</td></tr>
                ) : suppliers.map((s) => (
                  <tr key={s.id}>
                    {editingSupplierId === s.id ? (
                      <td colSpan={6}>
                        <form onSubmit={(e) => handleUpdateSupplier(e, s.id)} className="form-grid" style={{ padding: '1rem 0' }}>
                          <div className="form-field"><label>Name</label><input required value={editSupplierForm.name} onChange={(e) => setEditSupplierForm(p => ({ ...p, name: e.target.value }))} /></div>
                          <div className="form-field"><label>Email</label><input type="email" value={editSupplierForm.email} onChange={(e) => setEditSupplierForm(p => ({ ...p, email: e.target.value }))} /></div>
                          <div className="form-field"><label>Phone</label><input value={editSupplierForm.phone} onChange={(e) => setEditSupplierForm(p => ({ ...p, phone: e.target.value }))} /></div>
                          <div className="form-field"><label>Payment Terms (days)</label><input type="number" value={editSupplierForm.paymentTermsDays} onChange={(e) => setEditSupplierForm(p => ({ ...p, paymentTermsDays: Number(e.target.value) }))} /></div>
                          <div className="form-field">
                            <label>Status</label>
                            <select value={editSupplierForm.status} onChange={(e) => setEditSupplierForm(p => ({ ...p, status: e.target.value }))}>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                          <div className="form-field full-width form-actions">
                            <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}><FiSave size={14} /> Save</button>
                            <button type="button" className="btn btn-outline" style={{ width: 'auto' }} onClick={() => setEditingSupplierId(null)}>Cancel</button>
                          </div>
                        </form>
                      </td>
                    ) : (
                      <>
                        <td style={{ fontWeight: 600 }}>{s.name}</td>
                        <td>{s.email || '—'}</td>
                        <td>{symbolForCurrencyId(s.currencyId)} {currencies.find(c => c.id === s.currencyId)?.code || ''}</td>
                        <td>{s.paymentTermsDays} days</td>
                        <td><span className="badge" style={{ background: s.status === 'active' ? '#10b981' : '#9ca3af', color: 'white' }}>{s.status}</span></td>
                        <td>
                          <div className="actions">
                            <button className="action-icon edit" title="Edit" onClick={() => startEditSupplier(s)}>
                              <FiEdit2 size={16} />
                            </button>
                            <button className="action-icon delete" title="Delete" onClick={() => handleDeleteSupplier(s.id)}>
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Bills' && (
        <>
          <section className="page-section no-print">
            <div className="section-header">
              <h2>Bills</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => { if (showForm) closeBillForm(); else setShowForm(true); }}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Bill'}
              </button>
            </div>
            {!billWorkflowId && (
              <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
                No active approval workflow is set up for bills yet — draft bills can only be approved directly. Set one up under Workflow → Workflows (Applies To: Bill) to enable routed approval.
              </p>
            )}
            {showForm && (
              <form onSubmit={handleSubmitBill} style={{ marginBottom: '1.5rem' }}>
                {editingBillId && (
                  <div style={{ background: '#eff6ff', color: '#1e40af', padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>
                    Editing draft bill — changes recalculate VAT and totals from scratch.
                  </div>
                )}
                <div className="form-grid">
                  <div className="form-field">
                    <label>Supplier *</label>
                    <select required value={billForm.supplierId} onChange={(e) => setBillForm(p => ({ ...p, supplierId: e.target.value }))}>
                      <option value="">-- select --</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} ({symbolForCurrencyId(s.currencyId)})</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label>Supplier Reference</label><input value={billForm.supplierReference} onChange={(e) => setBillForm(p => ({ ...p, supplierReference: e.target.value }))} placeholder="Their invoice #" /></div>
                  <div className="form-field"><label>Bill Date *</label><input type="date" required value={billForm.billDate} onChange={(e) => setBillForm(p => ({ ...p, billDate: e.target.value }))} /></div>
                  <div className="form-field"><label>Due Date</label><input type="date" value={billForm.dueDate} onChange={(e) => setBillForm(p => ({ ...p, dueDate: e.target.value }))} /></div>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                    Line Items {billForm.supplierId && `(${billFormSymbol})`}
                  </label>
                  {billForm.lines.map((line, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <input required placeholder="Description" value={line.description} onChange={(e) => updateBillLine(idx, 'description', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                      <input required type="number" step="0.01" placeholder="Qty" value={line.quantity} onChange={(e) => updateBillLine(idx, 'quantity', e.target.value)} style={{ width: '70px', padding: '0.5rem' }} />
                      <input required type="number" step="0.01" placeholder="Price" value={line.unitPrice} onChange={(e) => updateBillLine(idx, 'unitPrice', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                      <select value={line.vatRateId} onChange={(e) => updateBillLine(idx, 'vatRateId', e.target.value)} style={{ width: '120px', padding: '0.5rem' }}>
                        <option value="">No VAT</option>
                        {vatRates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.ratePercentage}%)</option>)}
                      </select>
                      <span style={{ width: '80px', fontWeight: 600 }}>{billFormSymbol}{billLineTotal(line)}</span>
                      {billForm.lines.length > 1 && (
                        <button type="button" className="action-icon delete" onClick={() => removeBillLine(idx)}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addBillLine}>
                    <FiPlus size={14} /> Add Line
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: '1rem', fontWeight: 700, fontSize: '1.1rem' }}>
                  Subtotal: {billFormSymbol}{billFormTotal}
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>
                    {editingBillId ? 'Save Changes' : 'Create Draft Bill'}
                  </button>
                </div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Bill #</th><th>Supplier</th><th>Date</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                  ) : bills.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No bills yet</td></tr>
                  ) : bills.map((b) => {
                    const approval = approvalForBill(b.id);
                    return (
                      <tr key={b.id}>
                        <td style={{ fontWeight: 600 }}>{b.publicId}</td>
                        <td>{supplierName(b.supplierId)}</td>
                        <td>{new Date(b.billDate).toLocaleDateString()}</td>
                        <td>{symbolForCurrencyId(b.currencyId)}{Number(b.total).toFixed(2)}</td>
                        <td>
                          <span className="badge" style={{ background: b.status === 'paid' ? '#10b981' : b.status === 'approved' ? '#3b82f6' : '#f59e0b', color: 'white' }}>{b.status}</span>
                        </td>
                        <td>
                          <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewBill(b)}>
                              <FiEye size={14} /> View
                            </button>
                            <button className="action-icon edit" title="Copy to new bill" onClick={() => startCopyBill(b)}>
                              <FiCopy size={16} />
                            </button>
                            {approval && (
                              <span className="badge" style={{
                                background: approval.status === 'approved' ? '#10b981' : approval.status === 'rejected' ? '#ef4444' : approval.status === 'escalated' ? '#f59e0b' : '#3b82f6',
                                color: 'white',
                              }}>
                                Approval: {approval.status}
                              </span>
                            )}
                            {b.status === 'draft' && (
                              <>
                                <button className="action-icon edit" title="Edit" onClick={() => startEditBill(b)}>
                                  <FiEdit2 size={16} />
                                </button>
                                {!approval && billWorkflowId && (
                                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSubmitForApproval(b)}>
                                    <FiSend size={14} /> Submit for Approval
                                  </button>
                                )}
                                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleApproveBill(b.id)}>
                                  <FiCheck size={14} /> Approve
                                </button>
                                <button className="action-icon delete" title="Delete draft" onClick={() => handleDeleteDraftBill(b.id)}>
                                  <FiTrash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {selectedBill && (
            <section className="page-section bill-print-area">
              <div className="section-header no-print">
                <h2>Bill {selectedBill.publicId} — {supplierName(selectedBill.supplierId)}</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handlePrint}>
                    <FiPrinter size={14} /> Print
                  </button>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedBill(null)}>Close</button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid #1f2937' }}>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>{companyName}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Bill record</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>BILL</div>
                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>{selectedBill.publicId}</div>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  From supplier: <strong style={{ color: '#1f2937' }}>{supplierName(selectedBill.supplierId)}</strong>
                  {selectedBill.supplierReference ? ` · Ref: ${selectedBill.supplierReference}` : ''}
                </p>
                <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                  Bill date {new Date(selectedBill.billDate).toLocaleDateString()}
                  {selectedBill.dueDate ? ` · Due ${new Date(selectedBill.dueDate).toLocaleDateString()}` : ''}
                </p>
              </div>

              <div className="stats-row">
                <div className="stat-box"><div className="label">Subtotal</div><div className="value">{symbolForCurrencyId(selectedBill.currencyId)}{Number(selectedBill.subtotal).toFixed(2)}</div></div>
                <div className="stat-box"><div className="label">VAT</div><div className="value">{symbolForCurrencyId(selectedBill.currencyId)}{Number(selectedBill.vatTotal).toFixed(2)}</div></div>
                <div className="stat-box"><div className="label">Total</div><div className="value">{symbolForCurrencyId(selectedBill.currencyId)}{Number(selectedBill.total).toFixed(2)}</div></div>
                <div className="stat-box"><div className="label">Amount Paid</div><div className="value" style={{ color: '#10b981' }}>{symbolForCurrencyId(selectedBill.currencyId)}{Number(selectedBill.amountPaid || 0).toFixed(2)}</div></div>
                <div className="stat-box"><div className="label">Balance Due</div><div className="value" style={{ color: '#ef4444' }}>{symbolForCurrencyId(selectedBill.currencyId)}{(Number(selectedBill.total) - Number(selectedBill.amountPaid || 0)).toFixed(2)}</div></div>
              </div>

              {billLines === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>VAT</th><th>Line Total</th></tr></thead>
                    <tbody>
                      {billLines.map((l) => (
                        <tr key={l.id}>
                          <td>{l.description}</td>
                          <td>{l.quantity}</td>
                          <td>{symbolForCurrencyId(selectedBill.currencyId)}{Number(l.unitPrice).toFixed(2)}</td>
                          <td>{symbolForCurrencyId(selectedBill.currencyId)}{Number(l.vatAmount || 0).toFixed(2)}</td>
                          <td style={{ fontWeight: 600 }}>{symbolForCurrencyId(selectedBill.currencyId)}{Number(l.lineTotal).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'Expense Claims' && (
        <section className="page-section no-print">
          <div className="section-header"><h2>Expense Claims</h2></div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            This is the approver view — listing every employee's submitted claims. Employees submit their own claims via the self-service side, which needs an employee account genuinely linked to a login.
          </p>
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Claim #</th><th>Date</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {expenseClaims.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No expense claims yet</td></tr>
                ) : expenseClaims.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.publicId}</td>
                    <td>{new Date(c.claimDate).toLocaleDateString()}</td>
                    <td>{symbolForCurrencyId(c.currencyId)}{Number(c.total).toFixed(2)}</td>
                    <td>
                      <span className="badge" style={{ background: c.status === 'paid' ? '#10b981' : c.status === 'rejected' ? '#ef4444' : c.status === 'approved' ? '#3b82f6' : '#f59e0b', color: 'white' }}>{c.status}</span>
                    </td>
                    <td>
                      <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                        {(() => {
                          const approval = approvalForClaim(c.id);
                          return approval ? (
                            <span className="badge" style={{
                              background: approval.status === 'approved' ? '#10b981' : approval.status === 'rejected' ? '#ef4444' : approval.status === 'escalated' ? '#f59e0b' : '#3b82f6',
                              color: 'white',
                            }}>
                              Approval: {approval.status}
                            </span>
                          ) : null;
                        })()}
                        {c.status === 'submitted' && !approvalForClaim(c.id) && claimWorkflowId && (
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSubmitClaimForApproval(c)}>
                            <FiSend size={14} /> Submit for Approval
                          </button>
                        )}
                        {c.status === 'submitted' && (
                          <>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleApproveClaim(c.id)}>
                              <FiCheck size={14} /> Approve
                            </button>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleRejectClaim(c.id)}>
                              <FiX size={14} /> Reject
                            </button>
                          </>
                        )}
                        {c.status === 'approved' && (
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handlePayClaim(c.id)}>
                            <FiDollarSign size={14} /> Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Payments' && (
        <section className="page-section no-print">
          <div className="section-header"><h2>Record Payment</h2></div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Same as invoicing payments — these are real financial records and aren't deletable here.
          </p>
          <form onSubmit={handleRecordPayment} className="form-grid">
            <div className="form-field">
              <label>Supplier *</label>
              <select required value={paymentForm.supplierId} onChange={(e) => setPaymentForm(p => ({ ...p, supplierId: e.target.value, billId: '' }))}>
                <option value="">-- select --</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} ({symbolForCurrencyId(s.currencyId)})</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Bill to Pay *</label>
              <select required value={paymentForm.billId} onChange={(e) => setPaymentForm(p => ({ ...p, billId: e.target.value }))} disabled={!paymentForm.supplierId}>
                <option value="">-- select --</option>
                {supplierBills(paymentForm.supplierId).map((b) => <option key={b.id} value={b.id}>{b.publicId} — {symbolForCurrencyId(b.currencyId)}{Number(b.total).toFixed(2)}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Amount {paymentForm.supplierId ? `(${symbolForSupplier(paymentForm.supplierId)})` : ''} *</label><input required type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))} /></div>
            <div className="form-field">
              <label>Method</label>
              <select value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="direct_debit">Direct Debit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-field"><label>Date *</label><input required type="date" value={paymentForm.paidAt} onChange={(e) => setPaymentForm(p => ({ ...p, paidAt: e.target.value }))} /></div>
            <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Record Payment</button></div>
          </form>
          <div className="table-container" style={{ marginTop: '1.5rem' }}>
            <table className="data-table">
              <thead><tr><th>Supplier</th><th>Amount</th><th>Method</th><th>Date</th></tr></thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No payments recorded yet</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{supplierName(p.supplierId)}</td>
                    <td>{symbolForCurrencyId(p.currencyId)}{Number(p.amount).toFixed(2)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.paymentMethod?.replace('_', ' ')}</td>
                    <td>{new Date(p.paidAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default AP;